// Mirrors _PURE_DIGITS in outage-data-service's app/utils/iso_id_util.py. Only all-digit
// ISO-IDs are real ISO log identifiers; the service holds those unique across outages
// that are not Completed or Deleted. Free-text values such as "economics" or "PEAKERS"
// are reason codes and are reused on purpose.
const PURE_DIGITS = /^\d+$/;

export const isNumericIsoId = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  // Stored values are not consistently typed -- some rows hold versifyLogid as a JSON
  // number rather than a string -- so normalise before matching, as the service does.
  return PURE_DIGITS.test(String(value).trim());
};

// Mirrors EMBEDDED_WORKFLOW_TYPE in the service's iso_id_util.py. An Embedded outage is a
// derate nested inside another and refers to the same ISO ticket, so the service exempts
// it from ISO-ID uniqueness: it may carry its parent's numeric ISO-ID.
const EMBEDDED_WORKFLOW_TYPE = "embedded";

export const isEmbeddedWorkflow = (workflowTypeCode: string | null | undefined): boolean =>
  (workflowTypeCode ?? "").trim().toLowerCase() === EMBEDDED_WORKFLOW_TYPE;

// copiedIsoId is UI-only: it remembers the numeric ISO-ID a copied comment came with, so
// the ID can be shown or withheld as the copy's workflow changes. stripCopyMarkers drops
// it before the outage is sent to the service.
type CopyMarked = { versifyLogid?: unknown; copiedIsoId?: string };

// Shows each copied numeric ISO-ID while the workflow is Embedded and blanks it
// otherwise. Comments without copiedIsoId are returned as they are, so this is safe to
// run on any list.
export const applyCopiedIsoIds = <T>(comments: T[], workflowTypeCode: string | null | undefined): T[] => {
  if (!Array.isArray(comments)) return comments;
  const keepIsoIds = isEmbeddedWorkflow(workflowTypeCode);
  return comments.map((comment) => {
    const copiedIsoId = (comment as CopyMarked | null)?.copiedIsoId;
    if (!copiedIsoId) return comment;
    return { ...comment, versifyLogid: keepIsoIds ? copiedIsoId : "" };
  });
};

// Details carried over by Copy. Every comment travels, but a numeric ISO-ID identifies
// the source outage's ISO log entry, and the service rejects a second live outage
// claiming it unless that outage is Embedded. So the ID is kept only while the copy's
// workflow is Embedded, and is otherwise blanked for the user to replace.
// Generic over the element type rather than constraining it: a comment carrying no
// versifyLogid at all is valid, and a constraint of {versifyLogid?: unknown} would trip
// TypeScript's weak-type check on exactly those objects.
export const copyComments = <T>(
  comments: T[] | null | undefined,
  workflowTypeCode: string | null | undefined
): T[] => {
  if (!Array.isArray(comments)) return [];
  const marked = comments.map((comment) => {
    const isoId = (comment as CopyMarked | null)?.versifyLogid;
    if (!isNumericIsoId(isoId)) return comment;
    return { ...comment, copiedIsoId: String(isoId).trim() };
  });
  return applyCopiedIsoIds(marked, workflowTypeCode);
};

export const stripCopyMarkers = <T>(comments: T[]): T[] => {
  if (!Array.isArray(comments)) return comments;
  return comments.map((comment) => {
    if (!comment || typeof comment !== "object" || !("copiedIsoId" in comment)) return comment;
    const { copiedIsoId, ...rest } = comment as CopyMarked;
    return rest as T;
  });
};
